import React, {useCallback, useMemo} from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import MuiTooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import {BarElement, CategoryScale, Chart as ChartJS, LinearScale, Title, Tooltip, Legend} from 'chart.js';
import {Bar} from 'react-chartjs-2';
import {MinimalCutSet} from '../../../../utils/minimal-cut-sets';
import {FaultTreeNode} from '../../../../@types/faultTreeNode';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface BaseEventFrequencyChartProps {
    cutSets: MinimalCutSet[];
    nodeNameMap: Map<number, string>;
    nodeMap: Map<number, FaultTreeNode>;
    structuralHubIds?: Set<number>;
    onBarClick?: (baseEventId: number) => void;
    chartRef?: React.RefObject<ChartJS | null>;
}

const BaseEventFrequencyChart: React.FC<BaseEventFrequencyChartProps> = ({ cutSets, nodeNameMap, nodeMap, structuralHubIds, onBarClick, chartRef }) => {
    const frequencyData = useMemo((): Array<{ id: number; name: string; count: number; isBarrier: boolean; isHub: boolean }> => {
        const counts = new Map<number, number>();
        for (const cs of cutSets) {
            for (const id of cs.baseEventIds) {
                counts.set(id, (counts.get(id) || 0) + 1);
            }
        }
        return Array.from(counts.entries())
            .map(([id, count]) => ({
                id,
                name: nodeNameMap.get(id) || `ID:${id}`,
                count,
                isBarrier: nodeMap.get(id)?.nodeClass === 'BARRIER',
                isHub: structuralHubIds?.has(id) ?? false,
            }))
            .sort((a, b) => b.count - a.count);
    }, [cutSets, nodeNameMap, nodeMap, structuralHubIds]);

    const chartData = useMemo(() => ({
        labels: frequencyData.map((d) => d.name),
        datasets: [{
            label: 'Cut set appearances',
            data: frequencyData.map((d) => d.count),
            backgroundColor: frequencyData.map((d) =>
                d.isBarrier ? 'rgba(76, 175, 80, 0.7)'
                    : d.isHub ? 'rgba(237, 108, 2, 0.7)'
                        : 'rgba(25, 118, 210, 0.7)'
            ),
            borderColor: frequencyData.map((d) =>
                d.isBarrier ? 'rgba(76, 175, 80, 1)'
                    : d.isHub ? 'rgba(237, 108, 2, 1)'
                        : 'rgba(25, 118, 210, 1)'
            ),
            borderWidth: 1,
        }],
    }), [frequencyData]);

    const options = useMemo(() => ({
        indexAxis: 'y' as const,
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            title: { display: false },
            tooltip: {
                callbacks: {
                    label: (ctx: { raw: unknown }): string => {
                        const count = ctx.raw as number;
                        const pct = cutSets.length > 0 ? ((count / cutSets.length) * 100).toFixed(1) : '0';
                        return `${count} cut set${count !== 1 ? 's' : ''} (${pct}%)`;
                    },
                },
            },
        },
        scales: {
            x: {
                title: { display: true, text: 'Number of cut sets' },
                ticks: { precision: 0 },
            },
            y: {
                ticks: {
                    font: { size: 11 },
                    autoSkip: false,
                },
            },
        },
        onClick: (_event: unknown, elements: Array<{ index: number }>): void => {
            if (elements.length > 0 && onBarClick) {
                onBarClick(frequencyData[elements[0].index].id);
            }
        },
    }), [cutSets.length, frequencyData, onBarClick]);

    const handleExportCSV = useCallback((): void => {
        const header = ['Base Event', 'Cut Set Appearances', 'Percentage (%)'].join(',');
        const csvRows = frequencyData.map((d) => {
            const pct = cutSets.length > 0 ? ((d.count / cutSets.length) * 100).toFixed(1) : '0';
            return [
                `"${d.name.replace(/"/g, '""')}"`,
                d.count,
                pct,
            ].join(',');
        });
        const csv = [header, ...csvRows].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `base_event_frequency_${new Date().toISOString().slice(0, 10)}.csv`);
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, [frequencyData, cutSets.length]);

    const chartHeight = Math.max(200, frequencyData.length * 24 + 60);

    const hasHubs = frequencyData.some((d) => d.isHub);

    return (
        <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider', overflow: 'auto', maxHeight: 350 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="subtitle2">Basic Event Frequency</Typography>
                <MuiTooltip title="Export to CSV">
                    <IconButton size="small" onClick={handleExportCSV}>
                        <FileDownloadIcon fontSize="small" />
                    </IconButton>
                </MuiTooltip>
            </Stack>
            {hasHubs && (
                <Typography variant="caption" color="text.secondary">
                    Orange bars indicate structural hubs — events appearing in ≥50% of cut sets.
                </Typography>
            )}
            <Box sx={{ height: chartHeight, minHeight: 200 }}>
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                <Bar ref={chartRef as any} data={chartData} options={options} />
            </Box>
        </Box>
    );
};

export default BaseEventFrequencyChart;
